/**
 * RetailerDashboard (/retaileradmin)
 *
 * Headline counts over the retailer's estimates, the newest few, shares sent
 * to its stores in the last 30 days, and (managers) how many registrations
 * are waiting.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CalendarDays, TrendingUp, ClipboardList, Loader2, ExternalLink, Users, UserCheck, Send, Eye, EyeOff, MessageSquare } from 'lucide-react';
import { useRetailerContext } from './RetailerAdminContext';
import { fetchRetailerDashboard, type RetailerDashboardResponse } from '../../services/retailerAdminApi';
import { formatDateTime, formatDate, locationLabel, modeAndSpaceLabel } from '../../utils/estimateLabels';

export const RetailerDashboard: React.FC = () => {
  const { t, i18n } = useTranslation('retaileradmin');
  const ctx = useRetailerContext();
  const [data, setData] = useState<RetailerDashboardResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(false);
    fetchRetailerDashboard()
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [ctx.retailerId]);

  const stats = data?.stats;
  const cards = stats ? [
    { key: 'today',     value: stats.today,      icon: CalendarDays,  color: 'primary' },
    { key: 'yesterday', value: stats.yesterday,  icon: CalendarDays,  color: 'secondary' },
    { key: 'thisWeek',  value: stats.this_week,  icon: TrendingUp,    color: 'success' },
    { key: 'lastWeek',  value: stats.last_week,  icon: TrendingUp,    color: 'info' },
    { key: 'thisMonth', value: stats.this_month, icon: ClipboardList, color: 'warning' },
    { key: 'lastMonth', value: stats.last_month, icon: ClipboardList, color: 'secondary' },
    { key: 'total',     value: stats.total,      icon: ClipboardList, color: 'dark' },
  ] : [];

  return (
    <div>
      <div className="bg-white rounded-3 shadow-sm p-4 mb-3">
        <h2 className="fw-bold mb-1">{t('dashboard.title', { retailer: ctx.retailerName })}</h2>
        <p className="text-muted mb-0">{t('dashboard.subtitle')}</p>
        {!ctx.allLocations && ctx.locations.length > 0 && <p className="small text-muted mb-0 mt-1">{t('dashboard.yourLocations', { locations: ctx.locations.map(l => l.name).join(', ') })}</p>}
      </div>

      {error && <div className="alert alert-danger small">{t('dashboard.loadFailed')}</div>}
      {!data && !error && (
        <div className="text-center py-4"><Loader2 size={20} className="text-muted" style={{ animation: 'spin 1s linear infinite' }} /></div>
      )}

      {stats && (
        <div className="row g-2 mb-3">
          {cards.map(c => {
            const Icon = c.icon;
            return (
              <div key={c.key} className="col-6 col-sm-4 col-lg">
                <div className="bg-white rounded-3 border p-3 text-center h-100">
                  <Icon size={18} className={`text-${c.color} mb-1`} />
                  <div className="fw-bold fs-4 text-dark">{c.value}</div>
                  <div className="text-muted small">{t(`dashboard.stats.${c.key}`)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data && (
        <div className="row g-3">
          {ctx.canManage && (
            <div className="col-12 col-lg-4">
              <div className="bg-white rounded-3 shadow-sm p-3 h-100">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Users size={18} className="text-primary" />
                  <h6 className="fw-bold m-0">{t('dashboard.team')}</h6>
                </div>
                <div className="d-flex align-items-baseline gap-2">
                  <span className="fw-bold fs-3">{data.memberCount}</span>
                  <span className="text-muted small">{t('dashboard.members')}</span>
                </div>
                <div className="d-flex align-items-baseline gap-2">
                  <span className={`fw-bold fs-3 ${data.pendingCount ? 'text-warning' : ''}`}>{data.pendingCount ?? 0}</span>
                  <span className="text-muted small">{t('dashboard.pending')}</span>
                </div>
                {!!data.pendingCount && (
                  <Link to="/retaileradmin/users" className="btn btn-sm btn-warning d-inline-flex align-items-center gap-1 mt-2">
                    <UserCheck size={14} /> {t('dashboard.review')}
                  </Link>
                )}
              </div>
            </div>
          )}

          <div className={ctx.canManage ? 'col-12 col-lg-8' : 'col-12'}>
            <div className="bg-white rounded-3 shadow-sm p-3 h-100">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2">
                  <ClipboardList size={18} className="text-primary" />
                  <h6 className="fw-bold m-0">{t('dashboard.recent')}</h6>
                </div>
                <Link to="/retaileradmin/estimates" className="small">{t('dashboard.viewAll')}</Link>
              </div>
              {data.recent.length === 0 ? (
                <p className="text-muted small mb-0">{t('dashboard.recentEmpty')}</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <tbody>
                      {data.recent.map(est => (
                        <tr key={est.id}>
                          <td>
                            <div className="fw-semibold small">{est.first_name} {est.last_name}</div>
                            <div className="text-muted" style={{ fontSize: '0.75em' }}>{est.estimate_name || t('estimates.untitled')}{modeAndSpaceLabel(est.calculation_mode, est.space_type) ? ` · ${modeAndSpaceLabel(est.calculation_mode, est.space_type)}` : ''}</div>
                          </td>
                          <td className="small text-muted d-none d-sm-table-cell">{locationLabel(est.city, est.region)}</td>
                          <td className="small text-nowrap text-muted">{formatDateTime(est.created_at, i18n.language)}</td>
                          <td className="text-end">
                            <a href={`/estimate/${est.guid}?source=retailer`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary py-0 px-1" title={t('estimates.openTitle')}>
                              <ExternalLink size={14} />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="bg-white rounded-3 shadow-sm p-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <Send size={18} className="text-primary" />
                <h6 className="fw-bold m-0">{t('dashboard.shares')}</h6>
              </div>
              {data.shares.length === 0 ? (
                <p className="text-muted small mb-0">{t('dashboard.sharesEmpty')}</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead>
                      <tr className="text-muted small">
                        <th>{t('dashboard.shareColumns.estimate')}</th>
                        <th>{t('dashboard.shareColumns.customer')}</th>
                        <th className="d-none d-md-table-cell">{t('dashboard.shareColumns.store')}</th>
                        <th>{t('dashboard.shareColumns.sent')}</th>
                        <th className="text-center"><Eye size={14} /></th>
                        <th className="text-center"><MessageSquare size={14} /></th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {data.shares.map(s => (
                        <tr key={s.id}>
                          <td className="small">{s.estimateName || t('estimates.untitled')}</td>
                          <td className="small">
                            <div>{s.customerName}</div>
                            <div className="text-muted" style={{ fontSize: '0.75em' }}>{locationLabel(s.city, s.region)}</div>
                          </td>
                          <td className="small d-none d-md-table-cell">{s.locationName}</td>
                          <td className="small text-nowrap">{formatDate(s.sentAt, i18n.language)}</td>
                          <td className="text-center">{s.firstViewedAt ? <Eye size={14} className="text-success" /> : <EyeOff size={14} className="text-muted" />}</td>
                          <td className="text-center small">
                            {s.commentCount}
                            {s.unread > 0 && <span className="badge bg-primary ms-1">{t('dashboard.unread', { count: s.unread })}</span>}
                          </td>
                          <td className="text-end">
                            <a href={`/estimate/${s.guid}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary py-0 px-1" title={t('estimates.openTitle')}>
                              <ExternalLink size={14} />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetailerDashboard;
